import * as errors from "@superbuilders/errors"
import { XMLBuilder, XMLParser } from "fast-xml-parser"
import { serializeInner, serializeNode, serializeNodes } from "./html"
import {
	type AnyCondition,
	type AssessmentItem,
	AssessmentItemSchema,
	type AssessmentStimulus,
	AssessmentStimulusSchema,
	type BaseRule,
	type ConditionBranch,
	QtiBaseTypeSchema,
	QtiCardinalitySchema,
	type ResponseProcessing,
	type ResponseRule,
	type ScoringRule
} from "./schema"

// Clean, recursive representation of an XML node
export interface NormalizedNode {
	tagName: string
	attrs: Record<string, unknown>
	children: Array<NormalizedNode | string>
}

// --- Core XML & Node Utilities ---

function createXmlParser(): XMLParser {
	return new XMLParser({
		ignoreAttributes: false,
		attributeNamePrefix: "",
		allowBooleanAttributes: true,
		parseAttributeValue: true,
		parseTagValue: false, // CRITICAL: Preserve "000" as string, not convert to number 0
		trimValues: false,
		preserveOrder: true
	})
}

function _createXmlBuilder(): XMLBuilder {
	return new XMLBuilder({
		ignoreAttributes: false,
		attributeNamePrefix: "",
		suppressEmptyNode: true, // cleaner output for empty tags
		format: false
	})
}

function coerceString(value: unknown): string {
	if (value == null) return ""
	return String(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null
}

function normalizeNode(rawNode: unknown): NormalizedNode | string {
	if (rawNode == null) return ""
	if (!isRecord(rawNode)) return coerceString(rawNode)

	const textContent = rawNode["#text"]
	if (textContent != null) {
		return coerceString(textContent)
	}

	const tagName = Object.keys(rawNode).find((k) => k !== ":@")
	if (!tagName) return ""

	const attrsValue = rawNode[":@"]
	const attrs = isRecord(attrsValue) ? attrsValue : {}
	const rawChildren = Array.isArray(rawNode[tagName]) ? rawNode[tagName] : [rawNode[tagName]]

	return {
		tagName,
		attrs,
		children: rawChildren.map(normalizeNode).filter((n) => n !== "")
	}
}

function nodeToXml(node: NormalizedNode | string): string {
	// Use our HTML serializer for better MathML preservation
	if (typeof node === "string") return node
	return serializeNode(node)
}

function getInnerHtml(node: NormalizedNode): string {
	// Use our HTML serializer for inner content
	return serializeInner(node)
}

function findChild(node: NormalizedNode, tagName: string): NormalizedNode | undefined {
	const child = node.children.find((c) => typeof c !== "string" && c.tagName === tagName)
	return typeof child === "string" ? undefined : child
}

function findChildren(node: NormalizedNode, tagName: string): NormalizedNode[] {
	return node.children.filter((c): c is NormalizedNode => typeof c !== "string" && c.tagName === tagName)
}

function getTextContent(node: NormalizedNode | undefined): string {
	if (!node) return ""
	const firstChild = node.children[0]
	if (typeof firstChild === "string") return firstChild.trim()
	return ""
}

function findInteractionNodes(node: NormalizedNode, out: NormalizedNode[] = []): NormalizedNode[] {
	if (node.tagName.includes("-interaction")) {
		out.push(node)
	}
	for (const child of node.children) {
		if (typeof child !== "string") {
			findInteractionNodes(child, out)
		}
	}
	return out
}

function escapeAttr(value: string): string {
	return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

function attrsToString(attrs: Record<string, unknown>): string {
	const parts: string[] = []
	for (const [key, val] of Object.entries(attrs)) {
		const str = coerceString(val)
		if (str === "") continue
		parts.push(`${key}="${escapeAttr(str)}"`)
	}
	return parts.length ? ` ${parts.join(" ")}` : ""
}

function serializeWithInlinePlaceholders(
	node: NormalizedNode | string,
	inlineEmbeds: Record<string, ReturnType<typeof extractInlineChoiceInteraction>>,
	textEmbeds: Record<string, ReturnType<typeof extractTextEntryInteraction>>
): string {
	if (typeof node === "string") return node
	if (node.tagName === "qti-inline-choice-interaction") {
		const interaction = extractInlineChoiceInteraction(node)
		inlineEmbeds[interaction.responseIdentifier] = interaction
		return `<span data-qti-inline="${interaction.responseIdentifier}"></span>`
	}
	if (node.tagName === "qti-text-entry-interaction") {
		const interaction = extractTextEntryInteraction(node)
		textEmbeds[interaction.responseIdentifier] = interaction
		return `<span data-qti-text-entry="${interaction.responseIdentifier}"></span>`
	}
	const open = `<${node.tagName}${attrsToString(node.attrs)}>`
	const childrenHtml = node.children.map((c) => serializeWithInlinePlaceholders(c, inlineEmbeds, textEmbeds)).join("")
	const close = `</${node.tagName}>`
	return `${open}${childrenHtml}${close}`
}

// --- QTI Structure Extractors ---

function extractChoiceInteraction(node: NormalizedNode) {
	const promptNode = findChild(node, "qti-prompt")
	const promptHtml = promptNode ? getInnerHtml(promptNode) : ""

	const choiceNodes = findChildren(node, "qti-simple-choice")
	const choices = choiceNodes.map((choice) => {
		// Extract optional inline feedback
		const inlineFeedbackNode = findChild(choice, "qti-feedback-inline")
		const inlineFeedbackHtml = inlineFeedbackNode ? getInnerHtml(inlineFeedbackNode) : undefined

		// Build content HTML excluding inline feedback nodes
		const contentChildren = choice.children.filter(
			(c) => !(typeof c !== "string" && c.tagName === "qti-feedback-inline")
		)
		const contentHtml = serializeNodes(contentChildren)

		return {
			identifier: coerceString(choice.attrs.identifier),
			contentHtml,
			inlineFeedbackHtml
		}
	})

	return {
		type: "choiceInteraction" as const,
		responseIdentifier: coerceString(node.attrs["response-identifier"]),
		shuffle: Boolean(node.attrs.shuffle),
		minChoices: Number(node.attrs["min-choices"] ?? 0),
		maxChoices: Number(node.attrs["max-choices"] ?? 1),
		promptHtml,
		choices
	}
}

function extractInlineChoiceInteraction(node: NormalizedNode) {
	const choiceNodes = findChildren(node, "qti-inline-choice")
	const choices = choiceNodes.map((choice) => {
		const contentHtml = serializeInner(choice)
		return {
			identifier: coerceString(choice.attrs.identifier),
			contentHtml
		}
	})

	return {
		type: "inlineChoiceInteraction" as const,
		responseIdentifier: coerceString(node.attrs["response-identifier"]),
		shuffle: Boolean(node.attrs.shuffle),
		choices
	}
}

function extractTextEntryInteraction(node: NormalizedNode) {
	const expectedLengthAttr = node.attrs["expected-length"]
	const expectedLength = expectedLengthAttr ? Number(expectedLengthAttr) : undefined

	return {
		type: "textEntryInteraction" as const,
		responseIdentifier: coerceString(node.attrs["response-identifier"]),
		expectedLength: expectedLength !== undefined && !Number.isNaN(expectedLength) ? expectedLength : undefined,
		placeholderText: coerceString(node.attrs["placeholder-text"]) || undefined,
		patternMask: coerceString(node.attrs["pattern-mask"]) || undefined
	}
}

function extractOrderInteraction(node: NormalizedNode) {
	const promptNode = findChild(node, "qti-prompt")
	const promptHtml = promptNode ? getInnerHtml(promptNode) : ""

	const choiceNodes = findChildren(node, "qti-simple-choice")
	const choices = choiceNodes.map((choice) => {
		// Filter out inline feedback nodes from content
		const contentChildren = choice.children.filter(
			(c) => !(typeof c !== "string" && c.tagName === "qti-feedback-inline")
		)
		const contentHtml = serializeNodes(contentChildren)

		return {
			identifier: coerceString(choice.attrs.identifier),
			contentHtml,
			// Order interaction rarely displays inline feedback per-item
			inlineFeedbackHtml: undefined
		}
	})

	// Detect orientation (attribute OR class)
	let orientation: "vertical" | "horizontal" = "vertical"
	const attrOrientation = coerceString(node.attrs.orientation)
	const classAttr = coerceString(node.attrs.class)

	if (attrOrientation === "horizontal" || classAttr.includes("qti-orientation-horizontal")) {
		orientation = "horizontal"
	}

	return {
		type: "orderInteraction" as const,
		responseIdentifier: coerceString(node.attrs["response-identifier"]),
		shuffle: Boolean(node.attrs.shuffle),
		minChoices: Number(node.attrs["min-choices"] ?? 0),
		maxChoices: Number(node.attrs["max-choices"] ?? 0) || undefined,
		orientation,
		promptHtml,
		choices
	}
}

function extractGapMatchInteraction(node: NormalizedNode) {
	// Extract gap texts (draggable tokens)
	const gapTextNodes = findChildren(node, "qti-gap-text")
	const gapTexts = gapTextNodes.map((gt) => ({
		identifier: coerceString(gt.attrs.identifier),
		contentHtml: serializeInner(gt),
		matchMax: Number(gt.attrs["match-max"] ?? 0) // 0 = unlimited
	}))

	// Find all gaps in the content and build content HTML with placeholders
	const gaps: Array<{ identifier: string }> = []

	// Serialize content with gap placeholders
	function serializeWithGapPlaceholders(child: NormalizedNode | string): string {
		if (typeof child === "string") return child
		if (child.tagName === "qti-gap") {
			const gapId = coerceString(child.attrs.identifier)
			gaps.push({ identifier: gapId })
			return `<span data-qti-gap="${gapId}"></span>`
		}
		// Skip gap-text nodes in content serialization (they're in the source pool)
		if (child.tagName === "qti-gap-text") {
			return ""
		}
		const open = `<${child.tagName}${attrsToString(child.attrs)}>`
		const childrenHtml = child.children.map((c) => serializeWithGapPlaceholders(c)).join("")
		const close = `</${child.tagName}>`
		return `${open}${childrenHtml}${close}`
	}

	// Build content HTML from children (excluding gap-text nodes which are sources)
	const contentHtml = node.children
		.filter((c) => typeof c === "string" || c.tagName !== "qti-gap-text")
		.map((c) => serializeWithGapPlaceholders(c))
		.join("")

	return {
		type: "gapMatchInteraction" as const,
		responseIdentifier: coerceString(node.attrs["response-identifier"]),
		shuffle: Boolean(node.attrs.shuffle),
		gapTexts,
		gaps,
		contentHtml
	}
}

function extractMatchInteraction(node: NormalizedNode) {
	const promptNode = findChild(node, "qti-prompt")
	const promptHtml = promptNode ? getInnerHtml(promptNode) : ""

	// QTI has two <qti-simple-match-set> nodes
	const matchSets = findChildren(node, "qti-simple-match-set")

	const extractChoices = (setNode: NormalizedNode) => {
		const choiceNodes = findChildren(setNode, "qti-simple-associable-choice")
		return choiceNodes.map((c) => ({
			identifier: coerceString(c.attrs.identifier),
			matchMax: Number(c.attrs["match-max"]) || 1,
			contentHtml: serializeInner(c)
		}))
	}

	// First set = Sources, Second set = Targets (QTI convention)
	const firstSet = matchSets[0]
	const secondSet = matchSets[1]
	const sourceChoices = firstSet ? extractChoices(firstSet) : []
	const targetChoices = secondSet ? extractChoices(secondSet) : []

	return {
		type: "matchInteraction" as const,
		responseIdentifier: coerceString(node.attrs["response-identifier"]),
		shuffle: node.attrs.shuffle === "true",
		maxAssociations: Number(node.attrs["max-associations"]) || 0,
		sourceChoices,
		targetChoices,
		promptHtml
	}
}

function extractFeedbackBlock(node: NormalizedNode) {
	const contentBodyNode = findChild(node, "qti-content-body")
	const contentHtml = contentBodyNode ? getInnerHtml(contentBodyNode) : ""
	const showHide: "hide" | "show" = coerceString(node.attrs["show-hide"]) === "hide" ? "hide" : "show"

	return {
		outcomeIdentifier: coerceString(node.attrs["outcome-identifier"]),
		identifier: coerceString(node.attrs.identifier),
		showHide,
		contentHtml
	}
}

function extractResponseDeclarations(rootChildren: NormalizedNode[]) {
	const declNodes = rootChildren.filter((n) => n.tagName === "qti-response-declaration")

	return declNodes.map((node) => {
		const correctResponseNode = findChild(node, "qti-correct-response")
		const valueNodes = correctResponseNode ? findChildren(correctResponseNode, "qti-value") : []
		const values = valueNodes.map(getTextContent)

		// Optional mapping for map-response processing
		const mappingNode = findChild(node, "qti-mapping")
		let mapping:
			| {
					defaultValue: number
					lowerBound?: number
					upperBound?: number
					entries: Array<{ key: string; value: number }>
			  }
			| undefined
		if (mappingNode) {
			const defaultValueRaw = coerceString(mappingNode.attrs["default-value"])
			const lowerBoundRaw = coerceString(mappingNode.attrs["lower-bound"])
			const upperBoundRaw = coerceString(mappingNode.attrs["upper-bound"])
			const entryNodes = findChildren(mappingNode, "qti-map-entry")
			const entries = entryNodes.map((en) => {
				return {
					key: coerceString(en.attrs["map-key"]),
					value: Number(coerceString(en.attrs["mapped-value"]) || 0)
				}
			})
			mapping = {
				defaultValue: Number(defaultValueRaw || 0),
				lowerBound: lowerBoundRaw !== "" ? Number(lowerBoundRaw) : undefined,
				upperBound: upperBoundRaw !== "" ? Number(upperBoundRaw) : undefined,
				entries
			}
		}

		const cardinalityRaw = coerceString(node.attrs.cardinality)
		const cardinalityResult = QtiCardinalitySchema.safeParse(cardinalityRaw)
		if (!cardinalityResult.success) {
			throw errors.new(`invalid cardinality '${cardinalityRaw}' in response declaration`)
		}

		const baseTypeRaw = coerceString(node.attrs["base-type"])
		const baseTypeResult = QtiBaseTypeSchema.safeParse(baseTypeRaw)
		if (!baseTypeResult.success) {
			throw errors.new(`invalid base-type '${baseTypeRaw}' in response declaration`)
		}

		return {
			identifier: coerceString(node.attrs.identifier),
			cardinality: cardinalityResult.data,
			baseType: baseTypeResult.data,
			correctResponse: { values },
			mapping
		}
	})
}

function extractOutcomeDeclarations(rootChildren: NormalizedNode[]) {
	const declNodes = rootChildren.filter((n) => n.tagName === "qti-outcome-declaration")

	return declNodes.map((node) => {
		const defaultValueNode = findChild(node, "qti-default-value")
		const valueNode = defaultValueNode ? findChild(defaultValueNode, "qti-value") : undefined
		const defaultValue = valueNode ? { value: getTextContent(valueNode) } : undefined

		const cardinalityRaw = coerceString(node.attrs.cardinality)
		const cardinalityResult = QtiCardinalitySchema.safeParse(cardinalityRaw)
		if (!cardinalityResult.success) {
			throw errors.new(`invalid cardinality '${cardinalityRaw}' in outcome declaration`)
		}

		const baseTypeRaw = coerceString(node.attrs["base-type"])
		const baseTypeResult = QtiBaseTypeSchema.safeParse(baseTypeRaw)
		if (!baseTypeResult.success) {
			throw errors.new(`invalid base-type '${baseTypeRaw}' in outcome declaration`)
		}

		return {
			identifier: coerceString(node.attrs.identifier),
			cardinality: cardinalityResult.data,
			baseType: baseTypeResult.data,
			defaultValue
		}
	})
}

function parseCondition(node: NormalizedNode): AnyCondition | null {
	// A. Logical Operators
	if (node.tagName === "qti-and") {
		const conditions: AnyCondition[] = []
		for (const child of node.children) {
			if (typeof child !== "string") {
				const parsed = parseCondition(child)
				if (parsed) conditions.push(parsed)
			}
		}
		if (conditions.length === 0) return null
		return { type: "and", conditions }
	}

	if (node.tagName === "qti-or") {
		const conditions: AnyCondition[] = []
		for (const child of node.children) {
			if (typeof child !== "string") {
				const parsed = parseCondition(child)
				if (parsed) conditions.push(parsed)
			}
		}
		if (conditions.length === 0) return null
		return { type: "or", conditions }
	}

	if (node.tagName === "qti-not") {
		for (const child of node.children) {
			if (typeof child !== "string") {
				const parsed = parseCondition(child)
				if (parsed) return { type: "not", condition: parsed }
			}
		}
		return null
	}

	// B. Variable Extraction (Common logic for comparators)
	const variableNode = findChild(node, "qti-variable")
	const baseValueNode = findChild(node, "qti-base-value")

	const identifier = variableNode ? coerceString(variableNode.attrs.identifier) : undefined
	const value = baseValueNode ? getTextContent(baseValueNode) : undefined

	// C. Comparators
	if (node.tagName === "qti-match") {
		// Match against correct response
		if (findChild(node, "qti-correct") && identifier) {
			return { type: "match", variable: identifier, correct: true as const }
		}
		// Match against a specific value
		if (identifier && value !== undefined) {
			return { type: "matchValue", variable: identifier, value }
		}
	}

	// String Match (text entry with case sensitivity)
	if (node.tagName === "qti-string-match") {
		if (identifier && value !== undefined) {
			return {
				type: "stringMatch",
				variable: identifier,
				value,
				// QTI spec: case-sensitive defaults to true
				caseSensitive: node.attrs["case-sensitive"] !== "false"
			}
		}
	}

	// Member (set logic for multi-select)
	if (node.tagName === "qti-member") {
		if (identifier && value !== undefined) {
			return { type: "member", variable: identifier, value }
		}
	}

	// Equal: supports both map-response comparisons and correct response matching
	if (node.tagName === "qti-equal" || node.tagName === "qti-equal-rounded") {
		// Pattern 1: Equal with map-response (for summed scores)
		const mapResponseNode = findChild(node, "qti-map-response")
		const equalBaseValueNode = findChild(node, "qti-base-value")
		if (mapResponseNode && equalBaseValueNode) {
			return {
				type: "equalMapResponse",
				variable: coerceString(mapResponseNode.attrs.identifier),
				value: getTextContent(equalBaseValueNode)
			}
		}
		// Pattern 2: Equal with qti-correct (compare to correct response, like qti-match)
		// Used by text entry: <qti-equal><qti-variable/><qti-correct/></qti-equal>
		if (findChild(node, "qti-correct") && identifier) {
			return { type: "match", variable: identifier, correct: true as const }
		}
		// Pattern 3: Equal with qti-variable and qti-base-value (compare entered value to literal)
		// Used to branch feedback by entered value:
		//   <qti-equal><qti-variable identifier="RESP"/><qti-base-value base-type="integer">8</qti-base-value></qti-equal>
		if (identifier && value !== undefined && baseValueNode) {
			const baseType = coerceString(baseValueNode.attrs["base-type"])
			// Numeric base types use proper numeric comparison so "8" / "8.0" / "08" all match a target of 8.
			// qti-equal-rounded additionally captures rounding attributes so both sides are rounded before comparing.
			if (baseType === "integer" || baseType === "float") {
				const isRounded = node.tagName === "qti-equal-rounded"
				let rounding: { mode: "significantFigures" | "decimalPlaces"; figures: number } | undefined
				if (isRounded) {
					const rawMode = coerceString(node.attrs["rounding-mode"])
					const mode: "significantFigures" | "decimalPlaces" =
						rawMode === "decimalPlaces" ? "decimalPlaces" : "significantFigures"
					const figures = Number.parseInt(coerceString(node.attrs.figures) ?? "", 10)
					rounding = { mode, figures: Number.isFinite(figures) && figures > 0 ? figures : 1 }
				}
				return { type: "numericEqual", variable: identifier, value, baseType, rounding }
			}
			// Non-numeric base types (string / identifier / etc.) keep string-equality semantics
			return { type: "matchValue", variable: identifier, value }
		}
	}

	return null
}

// Parse actions (setOutcomeValue) from a branch node
function parseActions(node: NormalizedNode): BaseRule[] {
	const rules: BaseRule[] = []
	const setNodes = findChildren(node, "qti-set-outcome-value")
	for (const setNode of setNodes) {
		const valueNode = findChild(setNode, "qti-base-value")
		if (valueNode) {
			rules.push({
				type: "setOutcomeValue",
				identifier: coerceString(setNode.attrs.identifier),
				value: getTextContent(valueNode)
			})
		}
	}
	return rules
}

// RECURSIVE: Parse a processing rule (Condition Block OR Direct Action)
function parseResponseRule(node: NormalizedNode): ResponseRule | null {
	// Case 1: Direct Action (e.g. <qti-set-outcome-value>)
	if (node.tagName === "qti-set-outcome-value") {
		const valueNode = findChild(node, "qti-base-value")
		if (valueNode) {
			return {
				type: "action",
				action: {
					type: "setOutcomeValue",
					identifier: coerceString(node.attrs.identifier),
					value: getTextContent(valueNode)
				}
			}
		}
		return null
	}

	// Case 2: Response Condition (if/else block)
	if (node.tagName === "qti-response-condition") {
		const branches: ConditionBranch[] = []

		// 1. IF
		const ifNode = findChild(node, "qti-response-if")
		if (ifNode) {
			const condition = findConditionInBranch(ifNode)
			if (condition) {
				branches.push({
					condition,
					actions: parseActions(ifNode),
					nestedRules: findNestedRules(ifNode)
				})
			}
		}

		// 2. ELSE-IFs
		const elseIfNodes = findChildren(node, "qti-response-else-if")
		for (const elseIfNode of elseIfNodes) {
			const condition = findConditionInBranch(elseIfNode)
			if (condition) {
				branches.push({
					condition,
					actions: parseActions(elseIfNode),
					nestedRules: findNestedRules(elseIfNode)
				})
			}
		}

		// 3. ELSE
		const elseNode = findChild(node, "qti-response-else")
		if (elseNode) {
			branches.push({
				condition: undefined, // Else has no condition
				actions: parseActions(elseNode),
				nestedRules: findNestedRules(elseNode)
			})
		}

		if (branches.length > 0) {
			return {
				type: "condition",
				branches
			}
		}
	}

	return null
}

// Helper to find the condition node inside a response-if/else-if block
function findConditionInBranch(node: NormalizedNode): AnyCondition | null {
	const conditionTagNames = [
		"qti-match",
		"qti-and",
		"qti-or",
		"qti-not",
		"qti-equal",
		"qti-equal-rounded",
		"qti-string-match",
		"qti-member"
	]
	for (const child of node.children) {
		if (typeof child !== "string" && conditionTagNames.includes(child.tagName)) {
			return parseCondition(child)
		}
	}
	return null
}

// Helper to find nested CONDITION BLOCKS inside a branch
// Note: We only look for qti-response-condition, NOT qti-set-outcome-value
// because actions are already captured by parseActions() in the branch's `actions` array
function findNestedRules(node: NormalizedNode): ResponseRule[] {
	const nested: ResponseRule[] = []
	for (const child of node.children) {
		// Only parse nested response-condition blocks, not standalone actions
		if (typeof child !== "string" && child.tagName === "qti-response-condition") {
			const rule = parseResponseRule(child)
			if (rule) nested.push(rule)
		}
	}
	return nested
}

// Main processing extractor
function extractResponseProcessing(rootChildren: NormalizedNode[]): ResponseProcessing {
	const processingNode = rootChildren.find((n) => n.tagName === "qti-response-processing")
	if (!processingNode) {
		return { rules: [], scoring: { responseIdentifier: "", correctScore: 1, incorrectScore: 0 } }
	}

	const rules: ResponseRule[] = []
	for (const child of processingNode.children) {
		if (typeof child !== "string") {
			const rule = parseResponseRule(child)
			if (rule) rules.push(rule)
		}
	}

	// Legacy scoring extraction (unchanged logic for compatibility)
	let scoring: ScoringRule = { responseIdentifier: "", correctScore: 1, incorrectScore: 0 }
	const conditionNodes = findChildren(processingNode, "qti-response-condition")
	for (const conditionNode of conditionNodes) {
		const responseIfNode = findChild(conditionNode, "qti-response-if")
		const responseElseNode = findChild(conditionNode, "qti-response-else")

		if (responseIfNode && responseElseNode) {
			const andNode = findChild(responseIfNode, "qti-and")
			if (andNode) {
				const matchNode = findChild(andNode, "qti-match")
				const variableNode = matchNode ? findChild(matchNode, "qti-variable") : undefined
				if (variableNode) {
					const ifSetOutcomeNode = findChild(responseIfNode, "qti-set-outcome-value")
					const elseSetOutcomeNode = findChild(responseElseNode, "qti-set-outcome-value")
					const correctValueNode = ifSetOutcomeNode ? findChild(ifSetOutcomeNode, "qti-base-value") : undefined
					const incorrectValueNode = elseSetOutcomeNode ? findChild(elseSetOutcomeNode, "qti-base-value") : undefined
					scoring = {
						responseIdentifier: coerceString(variableNode.attrs.identifier),
						correctScore: Number(getTextContent(correctValueNode) || 1),
						incorrectScore: Number(getTextContent(incorrectValueNode) || 0)
					}
				}
			}
		}
	}

	return { rules, scoring }
}

// --- Main Parser Export ---

export function parseAssessmentItemXml(xml: string): AssessmentItem {
	// Validate input
	if (!xml || typeof xml !== "string") {
		throw errors.new("xml input must be a non-empty string")
	}

	const parser = createXmlParser()
	const parseResult = errors.trySync(() => {
		// Parse with preserveOrder flag as second parameter
		return parser.parse(xml, true)
	})

	if (parseResult.error) {
		throw errors.wrap(parseResult.error, "xml parse")
	}

	const raw = parseResult.data
	if (!Array.isArray(raw)) {
		throw errors.new("expected xml parser to output an array for preserveOrder")
	}

	const normalizedTree = raw.map(normalizeNode).filter((n): n is NormalizedNode => typeof n !== "string")

	const rootNode = normalizedTree.find((n) => n.tagName.endsWith("assessment-item"))
	if (!rootNode) {
		throw errors.new("qti assessment item not found in xml document")
	}

	const rootChildren = rootNode.children.filter((c): c is NormalizedNode => typeof c !== "string")
	const itemBodyNode = rootChildren.find((n) => n.tagName === "qti-item-body")

	type InteractionUnion =
		| ReturnType<typeof extractChoiceInteraction>
		| ReturnType<typeof extractInlineChoiceInteraction>
		| ReturnType<typeof extractTextEntryInteraction>
		| ReturnType<typeof extractOrderInteraction>
		| ReturnType<typeof extractGapMatchInteraction>
		| ReturnType<typeof extractMatchInteraction>
	type ContentBlock =
		| { type: "stimulus"; html: string }
		| {
				type: "richStimulus"
				html: string
				inlineEmbeds: Record<string, ReturnType<typeof extractInlineChoiceInteraction>>
				textEmbeds: Record<string, ReturnType<typeof extractTextEntryInteraction>>
		  }
		| { type: "interaction"; interaction: InteractionUnion }
	type FeedbackBlock = { outcomeIdentifier: string; identifier: string; showHide: "hide" | "show"; contentHtml: string }

	const contentBlocks: ContentBlock[] = []
	const feedbackBlocks: FeedbackBlock[] = []

	if (itemBodyNode) {
		for (const child of itemBodyNode.children) {
			if (typeof child === "string") {
				if (child.trim()) contentBlocks.push({ type: "stimulus" as const, html: child })
				continue
			}
			// Collect all interaction nodes within this subtree
			if (child.tagName === "qti-feedback-block") {
				feedbackBlocks.push(extractFeedbackBlock(child))
			} else {
				const interactionNodes = findInteractionNodes(child)
				const hasInline = interactionNodes.some(
					(n) => n.tagName === "qti-inline-choice-interaction" || n.tagName === "qti-text-entry-interaction"
				)
				if (hasInline) {
					const inlineEmbeds: Record<string, ReturnType<typeof extractInlineChoiceInteraction>> = {}
					const textEmbeds: Record<string, ReturnType<typeof extractTextEntryInteraction>> = {}
					const html = serializeWithInlinePlaceholders(child, inlineEmbeds, textEmbeds)
					contentBlocks.push({ type: "richStimulus" as const, html, inlineEmbeds, textEmbeds })
					continue
				}
				if (interactionNodes.length > 0) {
					for (const inode of interactionNodes) {
						if (inode.tagName === "qti-choice-interaction") {
							const interaction = extractChoiceInteraction(inode)
							contentBlocks.push({ type: "interaction" as const, interaction })
						} else if (inode.tagName === "qti-text-entry-interaction") {
							const interaction = extractTextEntryInteraction(inode)
							contentBlocks.push({ type: "interaction" as const, interaction })
						} else if (inode.tagName === "qti-order-interaction") {
							const interaction = extractOrderInteraction(inode)
							contentBlocks.push({ type: "interaction" as const, interaction })
						} else if (inode.tagName === "qti-gap-match-interaction") {
							const interaction = extractGapMatchInteraction(inode)
							contentBlocks.push({ type: "interaction" as const, interaction })
						} else if (inode.tagName === "qti-match-interaction") {
							const interaction = extractMatchInteraction(inode)
							contentBlocks.push({ type: "interaction" as const, interaction })
						} else {
							// Unknown interaction subtype: serialize as stimulus fallback
							contentBlocks.push({ type: "stimulus" as const, html: nodeToXml(inode) })
						}
					}
					continue
				}
				contentBlocks.push({ type: "stimulus" as const, html: nodeToXml(child) })
			}
		}
	}

	let normalizedItem = {
		identifier: coerceString(rootNode.attrs.identifier),
		title: coerceString(rootNode.attrs.title),
		timeDependent: Boolean(rootNode.attrs["time-dependent"]),
		xmlLang: coerceString(rootNode.attrs["xml:lang"]) || coerceString(rootNode.attrs["xml-lang"]) || "en",
		responseDeclarations: extractResponseDeclarations(rootChildren),
		outcomeDeclarations: extractOutcomeDeclarations(rootChildren),
		itemBody: { contentBlocks, feedbackBlocks },
		responseProcessing: extractResponseProcessing(rootChildren)
	}

	// Fallback: if scoring.responseIdentifier is empty, derive from first response declaration
	if (
		normalizedItem.responseProcessing.scoring.responseIdentifier === "" &&
		normalizedItem.responseDeclarations.length > 0
	) {
		const firstDecl = normalizedItem.responseDeclarations[0]
		if (firstDecl) {
			normalizedItem.responseProcessing.scoring.responseIdentifier = firstDecl.identifier
		}
	}

	const validation = AssessmentItemSchema.safeParse(normalizedItem)
	if (!validation.success) {
		// Format validation errors for better debugging
		const errorDetails = validation.error.issues.map((err) => `${err.path.join(".")}: ${err.message}`).join("; ")
		throw errors.new(`qti item validation: ${errorDetails}`)
	}

	return validation.data
}

// --- Stimulus Parser Export ---

/**
 * Parses a QTI 3.0 Assessment Stimulus XML document.
 *
 * Assessment Stimuli are standalone reading materials (articles, passages, etc.)
 * that can be referenced by one or more assessment items.
 *
 * @param xml - The raw XML string of the stimulus
 * @returns A validated AssessmentStimulus object
 * @throws If the XML is invalid or missing required elements
 *
 * @example
 * ```typescript
 * const stimulus = parseAssessmentStimulusXml(xmlString)
 * // stimulus.identifier - unique ID
 * // stimulus.title - human-readable title
 * // stimulus.bodyHtml - the HTML content to render
 * ```
 */
export function parseAssessmentStimulusXml(xml: string): AssessmentStimulus {
	// Validate input
	if (!xml || typeof xml !== "string") {
		throw errors.new("xml input must be a non-empty string")
	}

	const parser = createXmlParser()
	const parseResult = errors.trySync(() => {
		return parser.parse(xml, true)
	})

	if (parseResult.error) {
		throw errors.wrap(parseResult.error, "xml parse")
	}

	const raw = parseResult.data
	if (!Array.isArray(raw)) {
		throw errors.new("expected xml parser to output an array for preserveOrder")
	}

	const normalizedTree = raw.map(normalizeNode).filter((n): n is NormalizedNode => typeof n !== "string")

	// Find the root stimulus element
	const rootNode = normalizedTree.find(
		(n) => n.tagName === "qti-assessment-stimulus" || n.tagName.endsWith("assessment-stimulus")
	)
	if (!rootNode) {
		throw errors.new("qti assessment stimulus not found in xml document")
	}

	const rootChildren = rootNode.children.filter((c): c is NormalizedNode => typeof c !== "string")

	// Find the stimulus body
	const stimulusBodyNode = rootChildren.find((n) => n.tagName === "qti-stimulus-body")
	if (!stimulusBodyNode) {
		throw errors.new("qti-stimulus-body not found in stimulus document")
	}

	// Serialize the body content to HTML
	const bodyHtml = getInnerHtml(stimulusBodyNode)

	const normalizedStimulus = {
		identifier: coerceString(rootNode.attrs.identifier),
		title: coerceString(rootNode.attrs.title),
		xmlLang: coerceString(rootNode.attrs["xml:lang"]) || coerceString(rootNode.attrs["xml-lang"]) || "en",
		bodyHtml
	}

	const validation = AssessmentStimulusSchema.safeParse(normalizedStimulus)
	if (!validation.success) {
		const errorDetails = validation.error.issues.map((err) => `${err.path.join(".")}: ${err.message}`).join("; ")
		throw errors.new(`qti stimulus validation: ${errorDetails}`)
	}

	return validation.data
}
